import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import {
  SETTINGS_PATHS,
  SETTINGS_TITLE_KEYS,
  SUPPLIER_INQUIRY_TAB_TITLE_KEYS,
  supplierInquiriesTabPath,
  type SettingsSectionId,
  type SupplierInquiryTab,
} from "./paths"
import { useSettingsVisibility } from "./useSettingsVisibility"

/** Where a setting's value ends up, beyond its settings page. */
export type SettingsOutput =
  | "supplierEmails"
  | "customerEmails"
  /** What a supplier inquiry attaches, in either PDF format. */
  | "supplierInquiry"
  /** Only the questionnaire a PDF-only inquiry attaches (declaration_v1). */
  | "questionnairePdf"
  /** Declarations of conformity issued to customers. */
  | "customerDeclarations"

export const SETTINGS_OUTPUT_LABEL_KEYS: Record<
  SettingsOutput,
  TranslationKeys
> = {
  supplierEmails: "SETTINGS/USED_IN/SUPPLIER_EMAILS",
  customerEmails: "SETTINGS/USED_IN/CUSTOMER_EMAILS",
  supplierInquiry: "SETTINGS/USED_IN/SUPPLIER_INQUIRY",
  questionnairePdf: "SETTINGS/USED_IN/QUESTIONNAIRE_PDF",
  customerDeclarations: "SETTINGS/USED_IN/CUSTOMER_DECLARATIONS",
}

interface SettingsTargetDef {
  section: SettingsSectionId
  tab?: SupplierInquiryTab
  /** The section on its page, for `?focus=`. */
  focus: string
  /** Each output outside the page that prints or sends the value. */
  usedIn?: readonly SettingsOutput[]
}

/**
 * Every setting something links to, one entry each: the page and section it
 * is set on, and where its value ends up. Settings sections take their focus
 * anchor and "Used in" line from here, and every link to a setting (from the
 * inquiry draft, the setup checklist, the agent chat) is built from here, so
 * the two can't drift apart.
 *
 * Outputs, from the backend renderers: both inquiry PDFs print the logo,
 * sending address and contact person; only the PDF-only questionnaire prints
 * the PDF footer. A "Used in" line names only what the section's own text
 * doesn't already say.
 */
export const SETTINGS_TARGETS = {
  "org.details": {
    section: "general",
    focus: "details",
  },
  "org.logo": {
    section: "general",
    focus: "logo",
    usedIn: ["customerDeclarations", "supplierInquiry"],
  },
  "org.documentLanguage": {
    section: "general",
    focus: "document-language",
    usedIn: ["customerDeclarations"],
  },
  "email.sendingAddress": {
    section: "emailSending",
    focus: "sending-address",
    usedIn: ["supplierEmails", "customerEmails", "supplierInquiry"],
  },
  "email.signature": {
    section: "emailSending",
    focus: "signature",
  },
  "inquiry.firstEmail": {
    section: "supplierInquiries",
    tab: "emails",
    focus: "first-email",
  },
  "inquiry.reminderEmail": {
    section: "supplierInquiries",
    tab: "emails",
    focus: "reminder-email",
  },
  "inquiry.cc": { section: "supplierInquiries", tab: "emails", focus: "cc" },
  "inquiry.deadline": {
    section: "supplierInquiries",
    tab: "deadlines",
    focus: "deadline",
  },
  "inquiry.reminders": {
    section: "supplierInquiries",
    tab: "deadlines",
    focus: "reminders",
  },
  "inquiry.format": {
    section: "supplierInquiries",
    tab: "attachment",
    focus: "format",
  },
  "inquiry.footer": {
    section: "supplierInquiries",
    tab: "attachment",
    focus: "footer",
    usedIn: ["questionnairePdf"],
  },
  "inquiry.contactPerson": {
    section: "supplierInquiries",
    tab: "attachment",
    focus: "contact-person",
    usedIn: ["supplierInquiry"],
  },
  "declarations.signatureBox": {
    section: "conformityDeclarations",
    focus: "signature-box",
  },
  regulations: { section: "regulations", focus: "regulations" },
  signatures: { section: "signatures", focus: "signatures" },
} as const satisfies Record<string, SettingsTargetDef>

export type SettingsTargetId = keyof typeof SETTINGS_TARGETS

export function settingsTarget(id: SettingsTargetId): SettingsTargetDef {
  return SETTINGS_TARGETS[id]
}

// On a link from the setup checklist, so the section it lands on leads back.
const FROM_PARAM = "from"
const FROM_SETUP = "setup"

/** The setting's page (its tab, on Supplier inquiries), scrolled to it. */
export function settingsTargetPath(
  id: SettingsTargetId,
  { fromSetup = false }: { fromSetup?: boolean } = {},
): string {
  const { section, tab, focus } = settingsTarget(id)
  const page = tab ? supplierInquiriesTabPath(tab) : SETTINGS_PATHS[section]
  const query = new URLSearchParams({ focus })
  if (fromSetup) query.set(FROM_PARAM, FROM_SETUP)
  return `${page}?${query}`
}

/** Whether the URL's query is the setup checklist's link to this section. */
export function openedFromSetup(search: string, focus: string): boolean {
  const query = new URLSearchParams(search)
  return query.get("focus") === focus && query.get(FROM_PARAM) === FROM_SETUP
}

/** Where the setting lives, as titled in Settings: the page, then its tab. */
export function settingsTargetTrailKeys(
  id: SettingsTargetId,
): TranslationKeys[] {
  const { section, tab } = settingsTarget(id)
  return tab
    ? [SETTINGS_TITLE_KEYS[section], SUPPLIER_INQUIRY_TAB_TITLE_KEYS[tab]]
    : [SETTINGS_TITLE_KEYS[section]]
}

/** A link to the setting, and whether this user gets its page at all: a link
 *  shows only where its page does, so none leads to a page they can't open. */
export function useSettingsTarget(id: SettingsTargetId) {
  const visible = useSettingsVisibility()[settingsTarget(id).section]
  return {
    path: settingsTargetPath(id),
    trailKeys: settingsTargetTrailKeys(id),
    visible: visible === "visible",
  }
}
